import apiClient from './apiClient';
import {
  Action,
  PageResponseDto,
  RetryActionRequestDto,
  RtuActionStateUpdateDto,
} from '../types/actionTypes';

export interface ActionHistoryQuery {
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
  status?: string;
  actionType?: string;
  username?: string;
  rtuId?: number;
  fromUtc?: string;
  toUtc?: string;
}

const ACTION_ENDPOINTS = {
  LIST: '/actions',
  DETAIL: (actionId: number) => `/actions/${actionId}`,
  BY_RTU: (rtuId: number) => `/actions/rtu/${rtuId}`,
  RUNNING_RTUS: '/actions/running-rtus',
  RETRY: (actionId: number) => `/actions/${actionId}/retry`,
} as const;

const toQueryParams = (query: ActionHistoryQuery = {}): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    const apiKey = key === 'fromUtc' ? 'from' : key === 'toUtc' ? 'to' : key;
    params.set(apiKey, String(value));
  });
  return params;
};

const withPageFallback = <T,>(data: unknown, fallbackQuery?: ActionHistoryQuery): PageResponseDto<T> => {
  if (Array.isArray(data)) {
    return {
      content: data as T[],
      page: fallbackQuery?.page ?? 0,
      size: fallbackQuery?.size ?? data.length,
      totalElements: data.length,
      totalPages: 1,
    };
  }

  const typed = data as Partial<PageResponseDto<T>> | null | undefined;
  return {
    content: typed?.content ?? [],
    page: typed?.page ?? fallbackQuery?.page ?? 0,
    size: typed?.size ?? fallbackQuery?.size ?? 0,
    totalElements: typed?.totalElements ?? typed?.content?.length ?? 0,
    totalPages: typed?.totalPages ?? 0,
  };
};

export const actionHistoryService = {
  async fetchActions(query: ActionHistoryQuery = {}): Promise<PageResponseDto<Action>> {
    const response = await apiClient.get(ACTION_ENDPOINTS.LIST, { params: toQueryParams(query) });
    return withPageFallback<Action>(response.data, query);
  },

  async fetchActionById(actionId: number): Promise<Action> {
    const response = await apiClient.get(ACTION_ENDPOINTS.DETAIL(actionId));
    return response.data as Action;
  },

  async fetchActionsByRtuId(rtuId: number, query: ActionHistoryQuery = {}): Promise<PageResponseDto<Action>> {
    const response = await apiClient.get(ACTION_ENDPOINTS.BY_RTU(rtuId), { params: toQueryParams(query) });
    return withPageFallback<Action>(response.data, query);
  },

  async fetchRunningRtuStates(): Promise<RtuActionStateUpdateDto[]> {
    const response = await apiClient.get(ACTION_ENDPOINTS.RUNNING_RTUS);
    return (response.data ?? []) as RtuActionStateUpdateDto[];
  },

  async retryAction(actionId: number, payload?: RetryActionRequestDto): Promise<Action> {
    const response = await apiClient.post(ACTION_ENDPOINTS.RETRY(actionId), payload ?? {});
    return response.data as Action;
  },
};

export default actionHistoryService;
